import { createClient } from '@supabase/supabase-js';

// Get environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const supabaseServiceRoleKey = import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY;

// Validate required environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('Missing Supabase configuration. Using mock mode.');
}

/**
 * Public Supabase client for standard operations
 * Uses anonymous key with Row Level Security (RLS) policies
 */
export const supabase = createClient(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'placeholder-anon-key',
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  }
);

/**
 * Admin Supabase client for privileged operations
 * 
 * ⚠️ SECURITY WARNING ⚠️
 * The service role key bypasses Row Level Security (RLS) policies.
 * This should ONLY be used in secure server-side contexts.
 * 
 * For production, consider these alternatives:
 * 1. Use Supabase Edge Functions for admin operations
 * 2. Implement a backend API that uses the service role key server-side
 * 3. Use RLS policies with admin role claims instead
 * 
 * Current implementation is for development/demo purposes only.
 * Remove VITE_SUPABASE_SERVICE_ROLE_KEY from client-side builds in production.
 */
export const supabaseAdmin = supabaseServiceRoleKey
  ? createClient(
      supabaseUrl || 'https://placeholder.supabase.co',
      supabaseServiceRoleKey,
      {
        auth: {
          persistSession: false,
          autoRefreshToken: false,
        },
      }
    )
  : null;

// Export helper to check if Supabase is configured
export const isSupabaseConfigured = () => {
  return !!(supabaseUrl && supabaseAnonKey && supabaseUrl !== 'https://placeholder.supabase.co');
};

// Database types (to be expanded as schema is defined)
export interface Database {
  public: {
    Tables: {
      organizations: {
        Row: {
          id: string;
          name: string;
          trial_ends_at: string | null;
          subscription_status: SubscriptionStatus;
          stripe_customer_id: string | null;
          created_at: string;
          updated_at: string;
          mrr: number | null;
        };
        Insert: Omit<Database['public']['Tables']['organizations']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['organizations']['Insert']>;
      };
      users: {
        Row: {
          id: string;
          email: string;
          full_name: string;
          organization_id: string | null;
          role: UserRole;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['users']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['users']['Insert']>;
      };
      admin_users: {
        Row: {
          id: string;
          user_id: string;
          role: AdminRole;
          created_at: string;
        };
        Insert: Omit<Database['public']['Tables']['admin_users']['Row'], 'id' | 'created_at'>;
        Update: Partial<Database['public']['Tables']['admin_users']['Insert']>;
      };
      admin_audit_log: {
        Row: {
          id: string;
          admin_user_id: string;
          action: string;
          entity_type: string;
          entity_id: string;
          changes: Record<string, any>;
          created_at: string;
        };
        Insert: Omit<Database['public']['Tables']['admin_audit_log']['Row'], 'id' | 'created_at'>;
        Update: never;
      };
      subscriptions: {
        Row: {
          id: string;
          organization_id: string;
          plan_id: string;
          status: SubscriptionStatus;
          current_period_end: string;
          mrr: number;
          stripe_subscription_id: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['subscriptions']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['subscriptions']['Insert']>;
      };
    };
  };
}

// Enums and types
export type SubscriptionStatus = 'trial' | 'active' | 'canceled' | 'past_due' | 'expired';
export type UserRole = 'user' | 'admin' | 'owner';
export type AdminRole = 'super_admin' | 'support_admin' | 'finance_admin';
